/* global G */
import validate, { getMessageFromModelValidator } from 'lib/sequence/model/validate'
import sequenceComponentFindProxy from 'lib/sequence/component/children/find'
import { setKey } from 'lib/util'
import routeComposition from 'trait/composition/route'
import userExists from 'model/_shared/validator/userExists'
import validationResult from '../validateRedirect'

/**
 * Attempts to send a "password forgot" request according to the current model values.
 *
 * @param {Gaia.AppModule.Spec} module  the current module composition object
 * @returns {Promise<void>}             a pending promise object waiting for the request's result
 * @private
 */
const _submit = async (module) => {
  const moduleState = module[G.STATE]
  const model = module[G.MODEL]
  const httpAdapter = model[G.ADAPTER][G.HTTP]
  const { version, api } = model[G.PROPS]
  const { username } = model[G.DATA]
  const url = `/api/v${version}/${api}/password/forgotten/${username}`
  try {
    const result = await httpAdapter.put({ url })

    if (result.status === 'inactive') {
      // eslint-disable-next-line no-throw-literal
      throw { code: 403 }
    }
  } catch (error) {
    const { username: field } = sequenceComponentFindProxy(moduleState[G.ACTION][G.COMPONENT])
    const fieldState = field[G.STATE]

    const { code } = error
    fieldState.error = true
    moduleState[G.ERROR] = true
    fieldState.helperText = await getMessageFromModelValidator(model, 'username', code)
  }
}

/**
 * Validates the current model's data and, if there is no error, attempts to send a forgot request.
 *
 * @param {Gaia.AppModule.Spec} module                  the current module composition object
 * @returns {function(): function(...[*]): Promise<*>}  the call's arguments
 */
export default module => () => async (...args) => {
  const model = module[G.MODEL]
  const sessionState = module[G.ADAPTER][G.SESSION][G.STATE]

  const {
    form,
    item1,
    username,
  } = sequenceComponentFindProxy(module[G.STATE][G.ACTION][G.COMPONENT])

  await validate(model)(form || item1)

  if (!model[G.STATE][G.ERROR]) {
    const modelState = model[G.STATE]
    try {
      await userExists(model)(username[G.STATE], 'username')
      const passwordGenerated = modelState.passwordGenerated || modelState.oauth?.passwordGenerated
      setKey(!!passwordGenerated, 'passwordGenerated', sessionState)
    } catch (error) {
      const fieldState = username[G.STATE]
      fieldState.error = true
      modelState[G.ERROR] = true
      fieldState.helperText = await getMessageFromModelValidator(model, 'username', error.code)
    }
  }

  const result = validationResult(module)()(...args)

  if (!model[G.STATE][G.ERROR]) {
    // If the user has no password, redirect to the correct module/action
    sessionState.passwordGenerated
      ? setKey(routeComposition('guest', 'loginLink'), G.ROUTE, module[G.STATE])
      : await _submit(module)
  }

  return result
}
